import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Customer, MSAService } from '@/types';

interface ServiceConnectionModalProps {
  customer: Customer;
  availableServices: MSAService[];
  connectedServices: Array<{ id: number; service_id: number }>;
  onConnect: (serviceId: number) => void;
  onDisconnect: (serviceId: number) => void;
  onClose: () => void;
}

export const ServiceConnectionModal: React.FC<ServiceConnectionModalProps> = ({
  customer,
  availableServices,
  connectedServices,
  onConnect,
  onDisconnect,
  onClose,
}) => {
  const connectedServiceIds = connectedServices.map(cs => cs.service_id);
  const unconnectedServices = availableServices.filter(s => !connectedServiceIds.includes(s.id));

  return (
    <Dialog open={true}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>서비스 연결 관리</DialogTitle>
          <DialogDescription>
            {customer.customer_name}에 연결할 서비스를 선택하세요
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-6">
          {/* 연결된 서비스 목록 */}
          <div>
            <h3 className="text-lg font-semibold mb-3">연결된 서비스</h3>
            {connectedServices.length === 0 ? (
              <p className="text-gray-500 text-sm">연결된 서비스가 없습니다.</p>
            ) : (
              <div className="space-y-2">
                {connectedServices.map((cs) => {
                  const service = availableServices.find(s => s.id === cs.service_id);
                  if (!service) return null;
                  return (
                    <div key={cs.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{service.service_name}</p>
                        <p className="text-sm text-gray-500">{service.service_url}</p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDisconnect(service.id)}
                      >
                        연결 해제
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 사용 가능한 서비스 목록 */}
          <div>
            <h3 className="text-lg font-semibold mb-3">사용 가능한 서비스</h3>
            {unconnectedServices.length === 0 ? (
              <p className="text-gray-500 text-sm">연결 가능한 서비스가 없습니다.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {unconnectedServices.map((service) => (
                  <div key={service.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{service.service_name}</p>
                      <p className="text-sm text-gray-500">{service.category || '기타'}</p>
                      <p className="text-xs text-gray-400">{service.service_url}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => onConnect(service.id)}
                    >
                      연결
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

